// src/components/shared/EmptyState.jsx
import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Button,
  Paper
} from '@mui/material';
import {
  InboxOutlined
} from '@mui/icons-material';

/**
 * Componente para mostrar estados vacíos (sin datos)
 * 
 * @param {Object} props - Propiedades del componente
 * @param {React.ReactNode} props.icon - Icono a mostrar (opcional)
 * @param {string} props.title - Título principal del mensaje
 * @param {string} props.message - Mensaje descriptivo sobre el estado vacío
 * @param {string} props.actionText - Texto para el botón de acción (opcional)
 * @param {Function} props.onAction - Función a ejecutar al hacer clic en el botón
 * @param {boolean} props.usePaper - Si se debe envolver en un componente Paper
 * @param {Object} props.containerProps - Props adicionales para el contenedor Box
 * @param {Object} props.paperProps - Props adicionales para el Paper (si usePaper es true)
 */
const EmptyState = ({
  icon,
  title,
  message = "No hay datos para mostrar",
  actionText,
  onAction,
  usePaper = false,
  containerProps = {},
  paperProps = {},
}) => {
  // Componente interno con el contenido
  const Content = () => (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        py: 4,
        px: 2,
        textAlign: 'center',
        ...containerProps.sx 
      }} 
      {...containerProps}
    >
      {icon || <InboxOutlined sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />}
      
      {title && (
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
      )}
      
      <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, mb: 2 }}>
        {message}
      </Typography>
      
      {actionText && onAction && (
        <Button
          variant="outlined"
          onClick={onAction}
          sx={{ mt: 2 }}
        >
          {actionText}
        </Button>
      )}
    </Box>
  );

  // Si usePaper es true, envolver en un componente Paper
  if (usePaper) {
    return (
      <Paper 
        sx={{ 
          width: '100%', 
          ...paperProps.sx 
        }} 
        {...paperProps}
      >
        <Content />
      </Paper>
    );
  }

  // De lo contrario, devolver solo el contenido
  return <Content />;
};

EmptyState.propTypes = {
  icon: PropTypes.node,
  title: PropTypes.string,
  message: PropTypes.string,
  actionText: PropTypes.string,
  onAction: PropTypes.func,
  usePaper: PropTypes.bool,
  containerProps: PropTypes.object,
  paperProps: PropTypes.object,
};

export default EmptyState;